import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useCallback, useRef, useEffect, useState, } from 'react';
import { AppText, Label } from '../typography';
import { Button } from '../buttons';
import { Card } from '../card/card';
import styled from '@emotion/styled';
import { createPortal } from 'react-dom';
import { default as ReactCopyToClipboard } from 'react-copy-to-clipboard';
export const ShareLinkPopup = ({ open, onClose, artifactId, position, containerRef, link, refreshLink, companyName, anonymousShareEnabled, cardSize, }) => {
    const popupRef = useRef(null);
    const activeElement = useRef(document.activeElement);
    const closeAndReturnFocus = useCallback(() => {
        activeElement.current?.focus?.();
        onClose();
    }, [activeElement, onClose]);
    const handleKeydown = (e) => {
        if (e.key === 'Escape') {
            e.nativeEvent.stopImmediatePropagation();
            closeAndReturnFocus();
        }
    };
    const handleDocumentClick = useCallback((e) => {
        if (!popupRef.current?.contains(e.target)) {
            closeAndReturnFocus();
        }
    }, [popupRef, closeAndReturnFocus]);
    useEffect(() => {
        if (open) {
            refreshLink();
            document.addEventListener('click', handleDocumentClick, true);
            popupRef.current?.querySelector('input')?.focus();
        }
        return () => document.removeEventListener('click', handleDocumentClick, true);
    }, [open, handleDocumentClick, refreshLink]);
    const autoSelectInputText = (e) => {
        e.target.select();
    };
    const ignoreInputEvents = e => {
        e.preventDefault();
    };
    const idDescriptorText = artifactId ? 'artifact' : 'call';
    const warningText = anonymousShareEnabled
        ? `Caution. Anyone with this link can view this call and media.`
        : `Anyone on the ${companyName || 'company'} team can view this call and media.`;
    const showError = /error/gi.test(link || '');
    const showLoading = link === null;
    return createPortal(_jsx(Popup, { hidden: !open, onKeyDown: handleKeydown, ref: popupRef, top: position?.top, right: position?.right, children: _jsxs(PopupCard, { shadow: "xlarge", decoratorPosition: "side", "data-testid": "share-link-popup", size: cardSize, children: [showError && (_jsx(AppText, { as: "p", "data-testid": "share-link-popup-error", children: "There was a problem creating the link. Reopen this dialog to try again." })), !showError && (_jsxs(_Fragment, { children: [_jsxs(ControlsContainer, { children: [_jsx(Label, { screenReaderOnly: true, htmlFor: `shareable-${idDescriptorText}-details-link`, children: "Link to share" }), _jsx(ReadOnlyInput, { type: "text", id: `shareable-${idDescriptorText}-details-link`, "data-testid": "share-link-input", value: showLoading ? 'Generating link...' : link, onFocus: autoSelectInputText, onChange: ignoreInputEvents }), _jsx(CopyToClipboardPrimaryButton, { text: link || '', "data-testid": "share-link-button" })] }), _jsx(WarningText, { "data-testid": "share-link-help-text", children: warningText })] }))] }) }), containerRef?.current ?? document.body);
};
const CopyToClipboardPrimaryButton = ({ text, onCopy, 'data-testid': testId, }) => {
    const [buttonText, setButtonText] = useState('Copy');
    const handleCopyClick = useCallback(() => {
        setButtonText('Copied');
        onCopy?.();
        setTimeout(() => setButtonText('Copy'), 1500);
    }, [setButtonText, onCopy]);
    return (_jsx(ReactCopyToClipboard, { text: text, onCopy: handleCopyClick, children: _jsx(Button, { "data-testid": testId, style: { width: '105px' }, name: "copy", children: buttonText }) }));
};
const WarningText = styled.p `
    color: ${({ theme }) => theme.text.color.medium};
    font-size: ${({ theme }) => theme.text.size.small};
`;
const ControlsContainer = styled.div `
    display: flex;
`;
const ReadOnlyInput = styled.input `
    width: 100%;
    font-size: 1.1rem;
    margin-right: 1rem;
    line-height: inherit;
    border: 1px solid ${({ theme }) => theme.colors.grey90};
    padding: 0.5rem;
`;
const PopupCard = styled(Card) `
    width: ${props => props.size}px;
    display: grid;
    grid-gap: 0.25rem;
    line-height: 1.4;
    padding: 1.5rem 1.5rem 1rem 2rem;
    z-index: ${({ theme }) => theme.layers.ui};
`;
const Popup = styled.div `
    position: absolute;
    top: ${props => props.top}px;
    right: ${props => props.right}px;
    overflow: hidden;
    padding: 24px 24px 40px 24px;
`;
//# sourceMappingURL=share_link_popup.js.map